import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import * as r0 from "@/api/karateR0InstructorsApi.js";
import { ensureKarateR0Session } from "@/api/karateR0SessionBridge.js";
import { useAuth } from "@/context/AuthContext.jsx";

function eligibilityLabel(row) {
  if (row.isPubliclyEligible) return "Admissible";
  if (!row.profile.isPublic) return "Masqué";
  return "Non admissible";
}

function HanshiInstructorsR0Content() {
  const auth = useAuth();
  const { ui } = useKarateUi();
  const guard = ui.guard;
  const [sessionReady, setSessionReady] = useState(import.meta.env.VITEST === true || import.meta.env.MODE === "test");
  const [sessionDenied, setSessionDenied] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dojoFilter, setDojoFilter] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    personId: "",
    title: "",
    biography: "",
    photoUrl: "",
    specialties: "",
    yearsExperience: "",
    displayOrder: "0",
  });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    biography: "",
    photoUrl: "",
    specialties: "",
    yearsExperience: "",
    displayOrder: "0",
  });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);

  useEffect(() => {
    if (sessionReady || sessionDenied) return;
    let active = true;
    ensureKarateR0Session({ auth }).then((result) => {
      if (!active) return;
      if (result.ok) {
        setSessionReady(true);
        return;
      }
      setSessionDenied(true);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [auth, sessionReady, sessionDenied]);

  const loadData = useCallback(async () => {
    if (!sessionReady) return;
    setLoading(true);
    const [listRes, candRes, dojoRes] = await Promise.all([
      r0.fetchAdminInstructors({
        search: search || undefined,
        dojoId: dojoFilter || undefined,
        visibility: visibilityFilter || undefined,
      }),
      r0.fetchInstructorCandidates(),
      r0.fetchDojosForSelect(),
    ]);
    setLoading(false);
    if (listRes.status === 401 || candRes.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!listRes.ok) {
      setError(listRes.body?.error ?? "Impossible de charger les instructeurs.");
      return;
    }
    setError("");
    setInstructors(listRes.body.instructors ?? []);
    if (candRes.ok) setCandidates(candRes.body.candidates ?? []);
    if (dojoRes.ok) setDojos(dojoRes.body.dojos ?? []);
  }, [sessionReady, search, dojoFilter, visibilityFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleCreate(event) {
    event.preventDefault();
    if (createBusy) return;
    setNotice("");
    setError("");
    setCreateBusy(true);
    const result = await r0.createInstructorProfile({
      personId: createForm.personId,
      title: createForm.title.trim() || null,
      biography: createForm.biography.trim() || null,
      photoUrl: createForm.photoUrl.trim() || null,
      specialties: createForm.specialties.trim() || null,
      yearsExperience: createForm.yearsExperience ? Number(createForm.yearsExperience) : null,
      displayOrder: Number(createForm.displayOrder) || 0,
    });
    setCreateBusy(false);
    if (result.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!result.ok) {
      setError(result.body?.error ?? "Création impossible.");
      return;
    }
    setNotice("Profil instructeur créé.");
    setCreateOpen(false);
    setCreateForm({
      personId: "",
      title: "",
      biography: "",
      photoUrl: "",
      specialties: "",
      yearsExperience: "",
      displayOrder: "0",
    });
    await loadData();
  }

  function startEdit(row) {
    setEditId(row.id);
    setEditForm({
      title: row.profile.title ?? "",
      biography: row.profile.biography ?? "",
      photoUrl: row.profile.photoUrl ?? "",
      specialties: row.profile.specialties ?? "",
      yearsExperience: row.profile.yearsExperience?.toString() ?? "",
      displayOrder: row.profile.displayOrder?.toString() ?? "0",
    });
  }

  async function handleUpdate(event) {
    event.preventDefault();
    if (!editId || editBusy) return;
    setNotice("");
    setError("");
    setEditBusy(true);
    const result = await r0.updateInstructorProfile(editId, {
      title: editForm.title.trim() || null,
      biography: editForm.biography.trim() || null,
      photoUrl: editForm.photoUrl.trim() || null,
      specialties: editForm.specialties.trim() || null,
      yearsExperience: editForm.yearsExperience ? Number(editForm.yearsExperience) : null,
      displayOrder: Number(editForm.displayOrder) || 0,
    });
    setEditBusy(false);
    if (result.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!result.ok) {
      setError(result.body?.error ?? "Modification impossible.");
      return;
    }
    setNotice("Profil modifié.");
    setEditId(null);
    await loadData();
  }

  async function toggleVisibility(row) {
    setRowBusy(row.id);
    setNotice("");
    setError("");
    const next = !row.profile.isPublic;
    const result = await r0.setInstructorVisibility(row.id, next);
    setRowBusy(null);
    if (result.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!result.ok) {
      setError(result.body?.error ?? "Changement de visibilité impossible.");
      return;
    }
    setNotice(next ? "Profil publié." : "Profil masqué.");
    await loadData();
  }

  const sorted = useMemo(
    () =>
      [...instructors].sort((a, b) => {
        const order = (a.profile.displayOrder ?? 0) - (b.profile.displayOrder ?? 0);
        if (order !== 0) return order;
        return a.lastName.localeCompare(b.lastName, "fr");
      }),
    [instructors],
  );

  if (sessionDenied) {
    return (
      <p data-testid="instructors-session-denied">
        {guard.denied} <Link to="/connexion">{guard.login}</Link>
      </p>
    );
  }

  if (!sessionReady) {
    return <p data-testid="instructors-loading">Chargement…</p>;
  }

  return (
    <>
      <div className="accounts-toolbar" data-testid="instructors-toolbar">
        <input
          type="search"
          placeholder="Rechercher…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="instructors-search"
          aria-label="Rechercher un instructeur"
        />
        <select
          value={dojoFilter}
          onChange={(e) => setDojoFilter(e.target.value)}
          data-testid="instructors-dojo-filter"
          aria-label="Filtrer par dojo"
        >
          <option value="">Tous les dojos</option>
          {dojos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} ({d.city})
            </option>
          ))}
        </select>
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value)}
          data-testid="instructors-visibility-filter"
          aria-label="Filtrer par visibilité"
        >
          <option value="">Toutes les visibilités</option>
          <option value="public">Visible publiquement</option>
          <option value="hidden">Masqué</option>
          <option value="ineligible">Non admissible</option>
        </select>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => {
            setSearch("");
            setDojoFilter("");
            setVisibilityFilter("");
          }}
          data-testid="instructors-reset-filters"
        >
          Réinitialiser
        </button>
        <button
          type="button"
          className="btn-primary btn-sm"
          onClick={() => setCreateOpen((v) => !v)}
          data-testid="instructors-create-toggle"
          disabled={candidates.length === 0 && !createOpen}
        >
          {createOpen ? "Annuler" : "Nouveau profil"}
        </button>
      </div>

      {notice ? (
        <p className="form-notice" role="status">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p className="form-error" role="alert" data-testid="instructors-error">
          {error}
        </p>
      ) : null}

      {createOpen ? (
        <form className="create-account-form" onSubmit={handleCreate} data-testid="instructors-create-form">
          <h2>Nouveau profil instructeur</h2>
          <label>
            Sensei *
            <select
              required
              value={createForm.personId}
              onChange={(e) => setCreateForm((f) => ({ ...f, personId: e.target.value }))}
              data-testid="instructors-create-person"
            >
              <option value="">Choisir un Sensei…</option>
              {candidates.map((c) => (
                <option key={c.personId} value={c.personId}>
                  {c.firstName} {c.lastName} — {c.dojo?.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Titre ou grade
            <input
              value={createForm.title}
              onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="instructors-create-title"
            />
          </label>
          <label>
            Biographie
            <textarea
              value={createForm.biography}
              onChange={(e) => setCreateForm((f) => ({ ...f, biography: e.target.value }))}
              data-testid="instructors-create-biography"
            />
          </label>
          <button type="submit" className="btn-primary" disabled={createBusy} data-testid="instructors-create-submit">
            {createBusy ? "Création…" : "Créer"}
          </button>
        </form>
      ) : null}

      {editId ? (
        <form className="create-account-form" onSubmit={handleUpdate} data-testid="instructors-edit-form">
          <h2>Modifier le profil</h2>
          <label>
            Titre ou grade
            <input
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="instructors-edit-title"
            />
          </label>
          <label>
            Biographie
            <textarea
              value={editForm.biography}
              onChange={(e) => setEditForm((f) => ({ ...f, biography: e.target.value }))}
              data-testid="instructors-edit-biography"
            />
          </label>
          <button type="submit" className="btn-primary" disabled={editBusy} data-testid="instructors-edit-submit">
            {editBusy ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button type="button" className="btn-secondary" disabled={editBusy} onClick={() => setEditId(null)}>
            Annuler
          </button>
        </form>
      ) : null}

      {loading ? <p>Chargement…</p> : null}
      {!loading && sorted.length === 0 ? (
        <p className="accounts-empty" data-testid="instructors-empty">
          Aucun profil instructeur trouvé.
        </p>
      ) : null}

      {!loading && sorted.length > 0 ? (
        <div className="accounts-table-wrap">
          <table className="accounts-table" data-testid="instructors-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Titre</th>
                <th>Dojo</th>
                <th>Visibilité</th>
                <th>Admissibilité</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.id} data-testid={`instructor-row-${row.id}`}>
                  <td>
                    {row.firstName} {row.lastName}
                  </td>
                  <td>{row.profile.title ?? "—"}</td>
                  <td>{row.dojo ? `${row.dojo.name} (${row.dojo.city})` : "—"}</td>
                  <td data-testid={`instructor-visibility-${row.id}`}>
                    {row.profile.isPublic ? "Publié" : "Masqué"}
                  </td>
                  <td data-testid={`instructor-eligibility-${row.id}`}>{eligibilityLabel(row)}</td>
                  <td className="accounts-table__actions">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={rowBusy === row.id}
                      onClick={() => startEdit(row)}
                      data-testid={`instructor-edit-${row.id}`}
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      disabled={rowBusy === row.id}
                      onClick={() => toggleVisibility(row)}
                      data-testid={`instructor-toggle-visibility-${row.id}`}
                    >
                      {row.profile.isPublic ? "Masquer" : "Publier"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

export function HanshiInstructorsR0Page() {
  const { ui } = useKarateUi();
  const page = ui.adminPages.instructeurs;

  return (
    <AdminGuard>
      <PageSection title={page.title} subtitle={page.subtitleR0 ?? page.subtitle} testId="page-hanshi-instructors">
        <HanshiInstructorsR0Content />
      </PageSection>
    </AdminGuard>
  );
}
